// janus-engine/reactions/janus-ctx-trigger-readers.ts — lane RX (2026-09-28): TriggerReaders over Lane A's JanusCtx, so the ability
// pipeline can ask the janus_trigger() twin (trigger-dispatch.ts) instead of handing back on every authored trigger key:
//
//   const read = janusCtxTriggerReaders(ctx);
//   const { dispatched } = runTriggerDispatch(read, reactorRow, { trigger: 'triggers_when_caster_takes_any_damage' }, attackerRow,
//     (spec, caster) => { /* run spec.candidate.ability through the pipeline in trigger mode (ctx.trigger), then */ return caster; });
//
// Imports Lane A's context helpers; edits none of them. Every reader answers from the use's CURRENT state (member clones, this use's
// pending status rows and charge changes) — the state PHP's in-memory arrays hold when janus_trigger() runs mid-use. The PHP row
// rules are shared with the page adapter (php-row-rules.ts).
import { type JanusCtx, activeStatuses, ecId, itemField, record, upper, pendingStatusDefinitions } from '../ability/context.js';
import { TriggerInputUnavailable, type Row, type TriggerReaders } from './trigger-dispatch.js';
import { pageDistanceFt } from './page-trigger-readers.js';
import { frontEndPl, fullDefinition, heldTriggerCharges, inActiveCombat, naturalWeaponCount, opposingTeams, posCoord,
  triggerRangeGateInert } from './php-row-rules.js';

const scalar = (v: unknown): unknown => { let x = v; while (Array.isArray(x)) x = x[0]; return x; };
const EC_LEAN_ZERO = new Set(['reaction', 'mana', 'aps', 'luck', 'v_current', 'v_temp', 'movement_pct', 'encounter_creature_dead']);

export function janusCtxTriggerReaders(ctx: JanusCtx): TriggerReaders {
  // PHP's carrier casts receive value copies; this family's status pool sees its entry snapshot.
  // Earlier phases' additions are held; additions made by this family's own carriers appear in the next family.
  const pendingAtCall = new Set(ctx.statusesAdded);
  const live = (row: Row): Row => ctx.members.get(ecId(row)) ?? row;
  const value = (row: Row | null, key: string): unknown => ctx.read.value(row, key);
  const entries = (row: Row | null, key: string): unknown[] => ctx.read.entries(row, key);
  return {
    conditionsMet: (ctx.read as JanusCtx['read'] & { triggerConditionsMet?: (ability: Row, caster: Row) => boolean }).triggerConditionsMet,
    entries,
    value,
    itemField: (row, key) => itemField(row, key),
    definition: (id, allowLayerlessStatus) => fullDefinition(ctx.read.definition(id), allowLayerlessStatus),
    inCombat: row => inActiveCombat(ctx.encounter, live(row)),
    scalar: (row, field) => {
      const ec = record(live(row).encounter_creatures);
      const v = scalar(ec?.[field]);
      if (v === undefined || v === null || v === '') {
        if (ec?.encounter_creature_id && ec?.encounter_id && EC_LEAN_ZERO.has(field)) return 0;
        throw new TriggerInputUnavailable(`scalar-not-held:${field}`);
      }
      const n = Number(v);
      if (!Number.isFinite(n)) throw new TriggerInputUnavailable(`scalar-unreadable:${field}`);
      return Math.trunc(n);
    },
    // A charge this use already spent (adjust_charges) wins over the compiled wrapper.
    currentCharges: (entry, abilityId, caster) => {
      const holder = ecId(caster);
      const changed = [...ctx.chargesChanged].reverse().find(c => upper(c.holder) === holder && upper(c.item_id) === upper(abilityId));
      return changed ? changed.charges : heldTriggerCharges(entry, abilityId, live(caster), { value, entries, definition: id => ctx.read.definition(id) });
    },
    opposing: (a, b) => opposingTeams(live(a), live(b), value),
    distanceFt: (a, b) => pageDistanceFt({ value, entries }, live(a), live(b)),
    inRange: (_caster, _target, ability) => triggerRangeGateInert(ability, value),
    // The stored rows (a gapped container included) + this use's pending adds − its removals.
    statusItemIds: row => [...new Set(activeStatuses(ctx, live(row)).map(s => upper(s.status_item_id ?? itemField(record(s.status_arr), 'item_id'))))],
    statuses: row => {
      const member = live(row), id = ecId(member);
      const stored = ctx.read.statuses(member).filter(s => !s.tick_added && !ctx.statusesRemoved.some(r => upper(r.target) === id
        && (r.status_id ? upper(r.status_id) === upper(s.status_id) : upper(r.status_item_id) === upper(s.status_item_id))));
      const pending = ctx.statusesAdded.filter(s => pendingAtCall.has(s) && upper(s.target) === id).map(s => {
        const definition = pendingStatusDefinitions.get(s);
        const row = { ...s, status_target_id: s.target, status_caster_id: s.caster, status_arr: definition ?? ctx.read.definition(upper(s.status_item_id)) };
        if (definition) pendingStatusDefinitions.set(row, definition);
        return row;
      });
      return [...stored, ...pending];
    },
    vStatusId: row => {
      const v = record(record(live(row).computed)?.vitality)?.v_status;
      if (v === undefined || v === null || v === '') return '';
      return typeof v === 'string' ? v : String(record(record(v)?.items)?.item_id ?? scalar(v) ?? '');
    },
    naturalWeaponCount: row => naturalWeaponCount(live(row), entries),
    pl: definition => frontEndPl(definition, id => ctx.read.definition(id), record(ctx.encounter.catalog)),
    position: (row, axis) => posCoord(live(row), axis),
    encounterId: upper(record(ctx.encounter.encounters)?.encounter_id ?? ctx.encounter.encounter_id),
    roster: () => [...ctx.members.values()],
  };
}
